<script lang="ts">
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import OrderTabs from "./OrderTabs.svelte";
  import OrderProduct from "./OrderProduct.svelte";
  import DiscountSummary from "@shared/svelte/Components/Deal/DiscountSummary.svelte";
  import TaxSummary from "./TaxSummary.svelte";
  import Events from "./Events.svelte";
  import { Text } from "@getprovi/craft-svelte";
  import NotesAndBackup from "@retailer/svelte/Shared/NotesAndBackup.svelte";
  import PickupDeliverySummarySubheader from "@retailer/svelte/OrderDetail/PickupDeliverySummarySubheader.svelte";
  import ProductsHeader from "@retailer/svelte/OrderDetail/ProductsHeader.svelte";
  import RequestedItemsSection from "@retailer/svelte/OrderDetail/RequestedItemsSection.svelte";
  import { showOrderMessaging, OrderMessagingType } from "@retailer/services/show_order_messaging";
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";
  import List from "@shared/svelte/Components/Order/Messages/List.svelte";
  import Form from "@shared/svelte/Components/Order/Messages/Form.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import {
    isUpdatedOrder,
    vermontOrderReadyForPickup,
  } from "@shared/services/order_status_service";

  import type { SharedOrder, WarehouseSummaryType } from "@shared/types/Order";
  import type { CartProduct } from "@shared/types/ProductLine";

  export let order: SharedOrder;
  export let services;

  let showMessagesList = showOrderMessaging(order) === OrderMessagingType.MESSAGE_LIST;

  const hasNewMessages = new URLSearchParams(window.location.search).has("new_messages");
  const hasTaxesRollout = stagedRolloutCheck("marketplace__ohlq_order_taxes");

  let tabs = ["items", "events"];
  $: tabs =
    showMessagesList && $LayoutStore.isMobile
      ? ["items", "messages", "events"]
      : ["items", "events"];
  let selectedTab: { name: string } = {
    name: showMessagesList && $LayoutStore.isMobile && hasNewMessages ? "messages" : tabs[0],
  };

  function total(orderDetail) {
    if (!isUpdatedOrder(order, orderDetail.display_state) && orderDetail.original_total) {
      return orderDetail.original_total;
    } else {
      return orderDetail.post_tax_total > 0 ? orderDetail.post_tax_total : orderDetail.total;
    }
  }

  function warehouseTotal(orderDetail) {
    const updatedOrder = isUpdatedOrder(order, orderDetail.display_state);
    if (!updatedOrder && orderDetail.original_post_tax_total > 0) {
      return orderDetail.original_post_tax_total;
    } else if (!updatedOrder && orderDetail.original_total) {
      return orderDetail.original_total;
    } else {
      return orderDetail.post_tax_total > 0 ? orderDetail.post_tax_total : orderDetail.total;
    }
  }

  function subtotal(orderDetail) {
    const updatedOrder = isUpdatedOrder(order, orderDetail.display_state);
    if (!updatedOrder && orderDetail.original_subtotal) {
      return orderDetail.original_subtotal;
    } else {
      return orderDetail.subtotal;
    }
  }

  function taxTotal(orderDetail) {
    return orderDetail.tax_total;
  }

  const showPONumber = order.po_number;
  const showCashOnDelivery = order.distributor.sf_uses_cash_on_delivery && order.cash_on_delivery;
  const showSplitInvoices = order.distributor.sf_uses_split_invoices && order.split_invoices;

  const orderProducts = isUpdatedOrder(order, order.display_state)
    ? order.products
    : order.products.filter(p => !p.requested_item);
  const requestedItems = isUpdatedOrder(order, order.display_state)
    ? []
    : order.products.filter(p => p.requested_item);

  const productsByWarehouse: { [key: string]: CartProduct[] } = orderProducts.reduce(
    (products, product) => {
      const warehouseKey = product.warehouse_id || "delivery";
      products[warehouseKey] = products[warehouseKey] || [];
      products[warehouseKey].push(product);
      return products;
    },
    {},
  );

  const summaryByWarehouse: { [key: string]: WarehouseSummaryType } =
    order.warehouse_summaries.reduce((summaries, summary) => {
      const warehouseKey = summary.warehouse_id || "delivery";
      summaries[warehouseKey] = summary;
      return summaries;
    }, {});

  const pickupItemsExist = order.warehouse_summaries.some(summary => summary.for_pickup);

  const OHLQOrderPaymentMethodsEnabled = stagedRolloutCheck(
    "marketplace__ohlq_order_payment_methods",
  );

  let authService = getControlStateAuthenticationService();

  let hideAllDistributorInfo: boolean = false;
  let retailerUserAuthStatus: string;
  $: hideAllDistributorInfo = authService?.shouldHideAllDistributorInfo?.(
    $RetailerUserAuthenticationsStore,
    [order?.distributor?.id],
  );
  $: retailerUserAuthStatus = authService?.getRetailerUserAuthenticationStatus?.(
    $RetailerUserAuthenticationsStore,
    [order?.distributor?.id],
  );

  const disclaimerText = (orderDetail: WarehouseSummaryType) =>
    vermontOrderReadyForPickup(orderDetail)
      ? "*Final pricing and quantities are subject to change, depending on availability at pickup."
      : "Your total is not your final bill. Your final bill will come directly from your distributor through its regular method.";

  const productIsNotAvailable = (orderProduct: CartProduct, orderDetail: WarehouseSummaryType) => {
    return (
      isUpdatedOrder(order, orderDetail.display_state) &&
      services.Order.orderProductIsNotAvailable(orderProduct)
    );
  };

  const isExcludedProduct = (product: CartProduct, orderDetail: WarehouseSummaryType): boolean =>
    productIsNotAvailable(product, orderDetail) || product.status === 4 || product.requested_item;
</script>

<FizzStyles>
  {#if hideAllDistributorInfo && authService?.bannerComponent}
    <div class="fizz-stack-8">
      <svelte:component
        this={authService?.bannerComponent}
        authStatus={retailerUserAuthStatus}
        {authService}
      />
    </div>
  {/if}
  <div data-testid="order-summary-section" class="fizz-surface fizz-inset-square-16 fizz-stack-8">
    <div class="justify--space-between align--top fizz-flow-8" class:media={$LayoutStore.isDesktop}>
      <div class="fizz-flow-16">
        <div class="fizz-text-large">
          <span class="fizz-text-strong">Order Summary</span>
          <span class="fizz-text-subdued">({pluralize("Item", order.total_items, true)})</span>
        </div>

        {#if pickupItemsExist && !OHLQOrderPaymentMethodsEnabled}
          <PickupDeliverySummarySubheader {order} />
        {/if}
      </div>
      <div class="default-small--bold" class:show--md={!order.distributor.uses_warehouse_pickup}>
        Est total <FormatMoney input={total(order)} />
      </div>
    </div>

    {#if showPONumber || showCashOnDelivery || showSplitInvoices}
      <div class:fizz-flow-8={$LayoutStore.isMobile} class:fizz-flex-row={$LayoutStore.isDesktop}>
        {#if showPONumber}
          <p class="fizz-text-medium">PO #{order.po_number}</p>
        {/if}
        {#if showCashOnDelivery}
          <div class="fizz-flex-row-inline-4">
            <Icon type="Check" width={16} height={16} />
            <span>Cash on delivery</span>
          </div>
        {/if}
        {#if showSplitInvoices}
          <div class="fizz-flex-row-inline-4">
            <Icon type="Check" width={16} height={16} />
            <span>Separate invoice</span>
          </div>
        {/if}
      </div>
    {/if}
    {#if !order.distributor.uses_warehouse_pickup}
      <NotesAndBackup {order} />
    {/if}
  </div>
</FizzStyles>

<div class="background-white">
  <OrderTabs {order} {tabs} bind:selectedTab />
</div>

{#if selectedTab.name === "items"}
  {#each Object.entries(productsByWarehouse) as [warehouse, products]}
    <div
      data-testid="order-detail-products-{warehouse}"
      class="background-white"
      class:fizz-stack-8={$LayoutStore.isDesktop}
      class:fizz-border-bottom={$LayoutStore.isMobile}
    >
      {#if order.distributor.uses_warehouse_pickup}
        <ProductsHeader warehouseSummary={summaryByWarehouse[warehouse]} {order} />
      {/if}
      {#each products as product}
        <div>
          <OrderProduct
            {product}
            excludedProduct={isExcludedProduct(product, summaryByWarehouse[warehouse])}
            updatedOrder={isUpdatedOrder(order, summaryByWarehouse[warehouse].display_state)}
            Product={services.Product}
            distributor={order.distributor}
          />
        </div>
      {/each}
      {#if hasTaxesRollout}
        <div
          class="fizz-full-width fizz-flex-row fizz-justify-end order-detail-tax-summary fizz-inset-square-16"
        >
          <div
            class:grid-row={$LayoutStore.isMobile}
            style={$LayoutStore.isDesktop ? "width:50%;" : ""}
          >
            <TaxSummary
              total={warehouseTotal(summaryByWarehouse[warehouse])}
              subtotal={subtotal(summaryByWarehouse[warehouse])}
              taxTotal={taxTotal(summaryByWarehouse[warehouse])}
              {isExcludedProduct}
              locationOrderDetail={summaryByWarehouse[warehouse]}
              {products}
              orderProducts={null}
              distributor={order.distributor}
            />
          </div>
        </div>
        <div
          class="fizz-full-width fizz-flex-row fizz-justify-end fizz-inset-horizontal-16 fizz-inset-vertical-8"
        >
          <div
            class:grid-row={$LayoutStore.isMobile}
            style={$LayoutStore.isDesktop ? "width:50%;" : ""}
          >
            <div class="fizz-flex-row fizz-space-between">
              <Text color="neutral" semibold size="sm">Estimated order total</Text>
              <Text color="neutral" semibold size="sm">
                <FormatMoney input={warehouseTotal(summaryByWarehouse[warehouse])} />
              </Text>
            </div>
          </div>
        </div>
        <div class="fizz-flex-row fizz-justify-end fizz-inset-horizontal-16 fizz-inset-vertical-8">
          <Text color="neutral" size="xs">{disclaimerText(summaryByWarehouse[warehouse])}</Text>
        </div>
      {:else}
        <div class="padding--medium fizz-styles">
          <div
            class:fizz-flex-row={$LayoutStore.isDesktop}
            class:fizz-justify-end={$LayoutStore.isDesktop}
          >
            <div
              class:grid-row={$LayoutStore.isMobile}
              style={$LayoutStore.isDesktop ? "width:50%;" : ""}
            >
              <DiscountSummary
                total={warehouseTotal(summaryByWarehouse[warehouse])}
                subtotal={subtotal(summaryByWarehouse[warehouse])}
                taxTotal={taxTotal(summaryByWarehouse[warehouse])}
                {products}
                orderProducts={null}
                distributor={order.distributor}
              />
            </div>
          </div>
        </div>
      {/if}
      {#if requestedItems.length}
        <RequestedItemsSection {requestedItems} {order} Product={services.Product} />
      {/if}
    </div>
  {/each}
{/if}
<div>
  {#if selectedTab.name === "messages" && showMessagesList}
    <FizzStyles>
      <div class="fizz-hide-on-large">
        <List
          orderId={order.id}
          messageThreadId={order.message_thread_id}
          extraClasses={"fizz-stack-16"}
          compactMode={true}
        />
        <div class="fizz-border-top fizz-inset-square-16 fizz-relative">
          <Form orderId={order.id} messageThreadId={order.message_thread_id} />
        </div>
      </div>
    </FizzStyles>
  {/if}

  {#if selectedTab.name === "events"}
    <div class="order-timeline hide--md">
      <Events {order} OrderEvent={services.OrderEvent} />
    </div>
  {/if}
</div>
{#if authService?.authenticationModalComponent}
  <svelte:component this={authService?.authenticationModalComponent} />
{/if}
